import React, { createContext, useContext } from 'react';

import type { CrmApiPort, CrmScreenKey } from './CrmApiPort';
import type { MockClaimPort } from './mockPort';
import type { NavLinkComponent } from '../components';

/**
 * ค่าที่ host ให้มา กระจายลงหน้าจอที่อยู่ลึกโดยไม่ต้อง drill props
 *
 * ⚠️ โมดูลที่ต้องให้ host เตรียมบางอย่างไว้ก่อน แต่ไม่ประกาศไว้ใน props จะไปพังเอาตอน runtime
 *    → โมดูลนี้จึงประกาศทุกอย่างที่ต้องพึ่ง host ไว้ใน props และ throw
 *      พร้อมวิธีแก้ตั้งแต่ตอน mount ถ้าขาด
 */
export interface CRMHost {
  /** ช่องทางคุยกับ backend — host เป็นคนฉีดเข้ามา */
  api: CrmApiPort;

  /** ตัวตนผู้ใช้ — ใช้บันทึกว่าใครเป็นคนตัดสิน (FR-ADM-09) */
  userId: number;
  username: string;
  isSuperUser: boolean;

  /** หน่วยงานปัจจุบัน — เปลี่ยนแล้วหน้าจอโหลดใหม่เอง */
  divisionId: number;

  /** โหมดที่กำลังรันอยู่ */
  embed: 'ishealth' | 'standalone';

  /**
   * แหล่งข้อมูลที่ใช้อยู่
   *   live  CRM module ของ HIS จริง (/apis/CRM/)
   *   mock  ข้อมูลจำลองในเครื่อง — **โหมด standalone เท่านั้น**
   *
   * แยกจาก embed โดยตั้งใจ เพราะ standalone ต่อของจริงได้ และเดโมก็ยังต้องใช้ข้อมูลจำลอง
   */
  dataMode: 'live' | 'mock';

  /** ตัวจริงของข้อมูลจำลอง — ต้องมีเมื่อ dataMode === 'mock' เท่านั้น */
  mock?: MockClaimPort;

  /** เปลี่ยนค่านี้เพื่อสั่งให้ทุกหน้าจอโหลดข้อมูลใหม่ (ปุ่มรีเซ็ตบนแถบสาธิตใช้) */
  refreshKey?: number;

  /** host ตัดสินว่ากดลิงก์ข้ามหน้าแล้วไปไหน */
  linkComponent?: NavLinkComponent;
  onNavigate?: (to: CrmScreenKey) => void;
}

const CRMHostContext = createContext<CRMHost | null>(null);

export const CRMHostProvider: React.FC<{ value: CRMHost; children: React.ReactNode }> = ({
  value,
  children,
}) => <CRMHostContext.Provider value={value}>{children}</CRMHostContext.Provider>;

export function useCRMHost(): CRMHost {
  const host = useContext(CRMHostContext);
  if (!host) {
    throw new Error(
      'useCRMHost ถูกเรียกนอก <CRMBackoffice> — ' +
        'หน้าจอของ crm-backoffice ต้องอยู่ใต้ <CRMBackoffice> เสมอ',
    );
  }
  return host;
}
